import { useMemo } from 'react';

import { Permission, toPersianDigits } from '@bookora/shared';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import type { PermissionGroupResponse } from '@/api/generated/model';
import { useAuth } from '@/auth/token-store';
import { toast } from '@/lib/feedback';
import { useCan } from '@/permissions/use-can';

import {
  fetchAllStaff,
  fetchInvites,
  fetchRoles,
  fetchStaffWorkingHours,
  fetchTimeOff,
  rolesCatalogue,
  fetchSeatUsage,
  type Role,
  type SeatUsage,
  type StaffInvite,
  type StaffMember,
  type TimeOffRow,
  type WorkingHoursRow,
} from './api';
import { OWNER_ROLE_KEY, readGrantRefusal, type GrantabilityRules } from './permission-model';

/**
 * The staff section's reads.
 *
 * Keys start with the active clinic id for the same reason `api/query-keys.ts`
 * does: TanStack caches across a clinic switch, and a key of `['staff']` would
 * hand clinic B the roster of clinic A for as long as it stayed fresh.
 *
 * They live here rather than in `api/query-keys.ts` because that file belongs
 * to the shell; a feature that has to edit a shared file to add a screen is a
 * feature that collides with every other agent working in parallel.
 */
export const staffKeys = {
  all: (clinicId: string | null) => ['staff-section', clinicId] as const,
  list: (clinicId: string | null) => ['staff-section', clinicId, 'members'] as const,
  invites: (clinicId: string | null) => ['staff-section', clinicId, 'invites'] as const,
  seats: (clinicId: string | null) => ['staff-section', clinicId, 'seats'] as const,
  roles: (clinicId: string | null) => ['staff-section', clinicId, 'roles'] as const,
  catalogue: (clinicId: string | null) => ['staff-section', clinicId, 'catalogue'] as const,
  workingHours: (clinicId: string | null, staffId: string, branchId: string | null = null) =>
    ['staff-section', clinicId, 'working-hours', staffId, branchId ?? 'all'] as const,
  timeOff: (clinicId: string | null, staffId: string | null) =>
    ['staff-section', clinicId, 'time-off', staffId ?? 'all'] as const,
} as const;

/**
 * The signed-in user's own grant ceiling.
 *
 * `actorPermissions` is the effective set `/auth/me` returned for this token —
 * the same list `GrantCeilingService` reads off the verified access token, so
 * the two agree by construction rather than by coincidence.
 *
 * `isOwner` is the explicit exemption, decided by the ACTIVE membership's role
 * key. `StaffMembershipService` only ever returns active memberships, so a
 * membership present here is an active one; the server tests `isActive` as well
 * because it reads the row directly.
 */
export function useGrantCeiling(): GrantabilityRules {
  const { permissions, activeClinic } = useAuth();
  return useMemo(
    () => ({
      actorPermissions: permissions,
      isOwner: activeClinic?.roleKey === OWNER_ROLE_KEY,
    }),
    [permissions, activeClinic?.roleKey],
  );
}

export function useStaffList(): UseQueryResult<StaffMember[]> {
  const { activeClinicId } = useAuth();
  const canView = useCan(Permission.STAFF_VIEW);
  return useQuery({
    queryKey: staffKeys.list(activeClinicId),
    queryFn: ({ signal }) => fetchAllStaff(signal),
    enabled: canView && Boolean(activeClinicId),
    staleTime: 60_000,
  });
}

export function useInvites(): UseQueryResult<StaffInvite[]> {
  const { activeClinicId } = useAuth();
  const canView = useCan(Permission.STAFF_VIEW);
  return useQuery({
    queryKey: staffKeys.invites(activeClinicId),
    queryFn: ({ signal }) => fetchInvites(signal),
    enabled: canView && Boolean(activeClinicId),
    staleTime: 30_000,
  });
}

export function useSeatUsage(): UseQueryResult<SeatUsage> {
  const { activeClinicId } = useAuth();
  const canView = useCan(Permission.STAFF_VIEW);
  return useQuery({
    queryKey: staffKeys.seats(activeClinicId),
    queryFn: ({ signal }) => fetchSeatUsage(signal),
    enabled: canView && Boolean(activeClinicId),
    staleTime: 60_000,
  });
}

export function useRoles(): UseQueryResult<Role[]> {
  const { activeClinicId } = useAuth();
  const canView = useCan(Permission.STAFF_VIEW);
  return useQuery({
    queryKey: staffKeys.roles(activeClinicId),
    queryFn: ({ signal }) => fetchRoles(signal),
    enabled: canView && Boolean(activeClinicId),
    staleTime: 60_000,
  });
}

/**
 * The API's copy of the permission registry.
 *
 * Not what the checkboxes are drawn from — `@bookora/shared` is — but the only
 * way to notice that this bundle and the running API disagree about which keys
 * exist. See `catalogueDrift`.
 */
export function useCatalogue(): UseQueryResult<PermissionGroupResponse[]> {
  const { activeClinicId } = useAuth();
  const canView = useCan(Permission.STAFF_VIEW);
  return useQuery({
    queryKey: staffKeys.catalogue(activeClinicId),
    queryFn: ({ signal }) => rolesCatalogue(signal),
    enabled: canView && Boolean(activeClinicId),
    staleTime: 30 * 60_000,
  });
}

export function useWorkingHours(
  staffId: string | undefined,
  branchId: string | null = null,
): UseQueryResult<WorkingHoursRow[]> {
  const { activeClinicId } = useAuth();
  return useQuery({
    queryKey: staffKeys.workingHours(activeClinicId, staffId ?? '', branchId),
    queryFn: ({ signal }) => fetchStaffWorkingHours(staffId as string, branchId, signal),
    enabled: Boolean(staffId) && Boolean(activeClinicId),
    staleTime: 60_000,
  });
}

export function useTimeOff(staffId: string | undefined): UseQueryResult<TimeOffRow[]> {
  const { activeClinicId } = useAuth();
  return useQuery({
    queryKey: staffKeys.timeOff(activeClinicId, staffId ?? null),
    queryFn: ({ signal }) => fetchTimeOff(staffId ? { staffId } : {}, signal),
    enabled: Boolean(activeClinicId),
    staleTime: 60_000,
  });
}

/* -------------------------------------------------------------------------- */
/* Writes                                                                     */
/* -------------------------------------------------------------------------- */

export interface SectionMutationOptions {
  /**
   * Persian sentence shown on success. A function is read at the moment of
   * success, for a sentence that names what was acted on (the colleague whose
   * invite was resent) rather than what the screen showed when it last drew.
   */
  successMessage: string | (() => string);
  /** Extra keys to drop beyond the whole staff section. */
  onDone?: () => void;
  /**
   * Called with the keys the grant ceiling refused, so a screen can highlight
   * the offending checkboxes. When it is not supplied — or the failure is
   * something else — the ordinary Persian toast is shown instead.
   */
  onGrantRefused?: (refusal: { labels: string[]; count: number }) => void;
  /**
   * A screen's own reading of a failure it knows how to resolve — a full plan
   * that wants «ارتقای پلن», say. Returns `true` when it handled the error,
   * and the generic toast is then not shown on top of the screen's answer.
   */
  onFailure?: (error: unknown) => boolean;
}

/**
 * The grant ceiling's refusal, said out loud.
 *
 * `PERMISSION_GRANT_EXCEEDS_OWN` carries `details.ungrantable` precisely so the
 * panel can name the keys instead of showing «دسترسی ندارید» on a screen with
 * sixty checkboxes. The list is capped at ten server-side, so the count is
 * reported separately rather than implied by the list's length.
 */
export function grantRefusalMessage(refusal: { labels: string[]; count: number }): string {
  const shown = refusal.labels.join('، ');
  const rest =
    refusal.count > refusal.labels.length
      ? ` و ${toPersianDigits(refusal.count - refusal.labels.length)} مورد دیگه`
      : '';
  return `این دسترسی‌ها رو خودتون ندارید، پس نمی‌تونید به دیگران بدید: ${shown}${rest}`;
}

/**
 * Every write in this section, wrapped identically.
 *
 * The invalidation is deliberately coarse — the entire `staff-section` subtree
 * — because these writes are entangled: assigning a role changes the role's
 * headcount, inviting somebody changes the seat count, and deactivating the
 * last-but-one owner changes whether the remaining owner may be touched at
 * all. A precise invalidation here would be a correctness bug waiting for the
 * day someone adds a fourth relationship.
 *
 * The error path is not optional. `useCan` decided whether the button was
 * drawn; the server decides whether the write happens, and it can refuse
 * something the cached permission list still allows — a role edited in another
 * tab thirty seconds ago is exactly that case.
 */
export function useSectionMutation<TArgs, TResult>(
  mutationFn: (args: TArgs) => Promise<TResult>,
  { successMessage, onDone, onGrantRefused, onFailure }: SectionMutationOptions,
) {
  const queryClient = useQueryClient();
  const { activeClinicId } = useAuth();

  return useMutation({
    mutationFn,
    onSuccess: () => {
      toast.success(typeof successMessage === 'function' ? successMessage() : successMessage);
      void queryClient.invalidateQueries({ queryKey: staffKeys.all(activeClinicId) });
      onDone?.();
    },
    onError: (error: unknown) => {
      if (onFailure?.(error)) return;
      const refusal = readGrantRefusal(error);
      if (refusal) {
        // Named keys rather than the generic sentence. The screen may also
        // paint them; the toast still has to say something specific, because a
        // user who hit this reached past a locked checkbox and needs to know
        // which one.
        toast.errorBanner('این تغییر انجام نشد', new Error(grantRefusalMessage(refusal)));
        onGrantRefused?.(refusal);
        return;
      }
      toast.error(error);
    },
  });
}
